import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
export const frontmatter = {
  "title": "Access",
  "lead": "Globally, 88% of children complete primary school, 78% of adolescents complete lower secondary school and 59% of youth complete upper secondary school."
};
function _createMdxContent(props) {
  const _components = {
    a: "a",
    blockquote: "blockquote",
    h2: "h2",
    li: "li",
    p: "p",
    strong: "strong",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  }, {Figure, Highlight, HighlightByIndex, IndicatorReference, PageLink, RelatedMaterial, ScrollyArea, ScrollySection, ScrollySticky, Tooltip} = _components;
  if (!Figure) _missingMdxReference("Figure", true);
  if (!Highlight) _missingMdxReference("Highlight", true);
  if (!HighlightByIndex) _missingMdxReference("HighlightByIndex", true);
  if (!IndicatorReference) _missingMdxReference("IndicatorReference", true);
  if (!PageLink) _missingMdxReference("PageLink", true);
  if (!RelatedMaterial) _missingMdxReference("RelatedMaterial", true);
  if (!ScrollyArea) _missingMdxReference("ScrollyArea", true);
  if (!ScrollySection) _missingMdxReference("ScrollySection", true);
  if (!ScrollySticky) _missingMdxReference("ScrollySticky", true);
  if (!Tooltip) _missingMdxReference("Tooltip", true);
  return _jsxs(_Fragment, {
    children: [_jsx(Figure, {
      id: "HERO_ACCESS"
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsx(_components.p, {
        children: "The world failed on its promise for all children to go to primary school by 2015. The new education goal, SDG 4, has set the level of ambition a step higher, calling for all young people to complete secondary school by the 2030 deadline. To achieve this, all children of school starting age should have begun school in 2018, but in reality, only 70% did so in low-income countries for instance."
      }), "\n"]
    }), "\n", _jsx(_components.h2, {
      id: "pre-school-attendance",
      children: "Pre-school attendance"
    }), "\n", _jsx(IndicatorReference, {
      id: "4.2.2",
      children: _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Attending pre-school"
        }), " is a crucial starting point for children's journey through education. Yet, in low-income countries, only 48% of children are enrolled in the year before they reach the official age for entry into primary education."]
      })
    }), "\n", _jsx(_components.p, {
      children: "Globally, participation in the year before primary school increased from 64% in 2003 to 75% in 2024, driven mainly by gains in the early 2000s. Progress has largely stalled since 2015, leaving many children without access to pre-primary education."
    }), "\n", _jsx(_components.p, {
      children: "Some countries have seen vast increases. In Azerbaijan, for example, the participation rate increased from 16% in 2000 to 83% in 2024. In the Lao PDR, it increased from 10% to 67% over the same period."
    }), "\n", _jsxs(_components.p, {
      children: ["Both the ", _jsx(HighlightByIndex, {
        index: "0",
        children: "Dominican Republic"
      }), " and ", _jsx(HighlightByIndex, {
        index: "1",
        children: "Senegal"
      }), " highlighted below are examples of countries that have seen a steady increase in pre-primary attendance, even though there are huge gaps between the two today. Select your country to see its progress."]
    }), "\n", _jsx(Figure, {
      id: "PREPRIM"
    }), "\n", _jsx(_components.h2, {
      id: "over-age-participation",
      children: "Over-age participation"
    }), "\n", _jsx(IndicatorReference, {
      id: "4.1.5",
      children: _jsxs(_components.p, {
        children: ["We do not talk enough about the fact that in a number of countries more than a quarter of primary school students are ", _jsx(_components.strong, {
          children: "over-age"
        }), " when they enrol, meaning they are significantly older than students in their grade are supposed to be. This has significant implications for their future, increasing the likelihood of further repeating grades, failing exams and eventually dropping out of school."]
      })
    }), "\n", _jsxs(_components.p, {
      children: ["This graph looks at ", _jsx(_components.strong, {
        children: "education participation"
      }), ", showing how old children, adolescents and youth are when they attend pre-primary, primary, lower and upper secondary, as well as post-secondary education. The spread of colour indicates how many children are over-age for their education level. The grey shaded area shows the percentage of children and adolescents who are out of school by age."]
    }), "\n", _jsxs(ScrollyArea, {
      children: [_jsx(ScrollySticky, {
        children: _jsx(Figure, {
          id: "OVERAGE"
        })
      }), _jsx(ScrollySection, {
        id: "KHM",
        children: _jsxs(_components.p, {
          children: ["In ", _jsx(Highlight, {
            children: "Cambodia"
          }), ", most children should be in school at 10\nyears old, yet 21% are still out of school at that age. Many are too old for\ntheir grade. There are 14-year-olds who are still attending primary school,\nfor instance."]
        })
      }), _jsx(ScrollySection, {
        id: "LBR",
        children: _jsxs(_components.p, {
          children: ["The over-age rate is even higher in countries such as", " ", "\n", _jsx(Highlight, {
            children: "Liberia"
          }), " with 28% of 10 year olds in pre-school and\n20% of 18 year olds still in primary school."]
        })
      }), _jsx(ScrollySection, {
        id: "NPL",
        children: _jsxs(_components.p, {
          children: [_jsx(Highlight, {
            children: "Nepal"
          }), " faces the same challenge"]
        })
      }), _jsx(ScrollySection, {
        id: "MWI",
        children: _jsxs(_components.p, {
          children: ["as does ", _jsx(Highlight, {
            children: "Malawi"
          }), "."]
        })
      })]
    }), "\n", _jsx(_components.h2, {
      id: "out-of-school-children",
      children: "Out-of-school children"
    }), "\n", _jsxs(IndicatorReference, {
      id: "4.1.4",
      children: [_jsxs(_components.p, {
        children: ["Globally, there are 273 million ", _jsx(_components.strong, {
          children: "out of school"
        }), " children and youth. A ", _jsx(_components.a, {
          href: "https://www.education-estimates.org",
          children: "new model developed by the UNESCO Institute for Statistics and the Global Education Monitoring Report"
        }), " suggests that the out-of-school population has been increasing for the past few years."]
      }), _jsx(_components.p, {
        children: "Conflict is a key reason for stagnation. Unfortunately conflict also means that the availability and quality of data deteriorates, although some countries continue to report estimates."
      }), _jsxs(ScrollyArea, {
        children: [_jsx(ScrollySticky, {
          children: _jsx(Figure, {
            id: "OOSRATE"
          })
        }), _jsx(ScrollySection, {
          id: "ECU,PRY",
          children: _jsxs(_components.p, {
            children: [_jsx(Highlight, {
              children: "Ecuador"
            }), " and ", _jsx(Highlight, {
              children: "Paraguay"
            }), " show very\ndifferent directions of progress. In lower and upper secondary education, the\nout-of-school rate in Ecuador has plummeted, while it has climbed in\nParaguay."]
          })
        }), _jsx(ScrollySection, {
          id: "ERI,ETH",
          children: _jsxs(_components.p, {
            children: [_jsx(Highlight, {
              children: "Eritrea"
            }), " and ", _jsx(Highlight, {
              children: "Ethiopia"
            }), " also show\nvery different rates of progress. The after-effects of conflict in Eritrea\nvisibly took a toll on education access, while Ethiopia has prioritised\neducation in its budget, which led to the rapid reduction in the primary out-of-school\nrate."]
          })
        })]
      })]
    }), "\n", _jsx(_components.p, {
      children: "Another reason for a lack of progress in reducing out-of-school numbers is population growth."
    }), "\n", _jsxs(_components.p, {
      children: ["In sub-Saharan Africa, the primary ", _jsx(HighlightByIndex, {
        index: "2",
        children: "school-aged population"
      }), " has nearly doubled between 2000 and 2024. As a result, even though the ", _jsx(HighlightByIndex, {
        index: "0",
        children: "rate of out-of-school children"
      }), " has nearly halved during this period, the ", _jsx(HighlightByIndex, {
        index: "1",
        children: "number of children out-of-school"
      }), " has barely changed."]
    }), "\n", _jsx(Figure, {
      id: "OOSREL"
    }), "\n", _jsx(_components.h2, {
      id: "completion",
      children: "Completion"
    }), "\n", _jsxs(IndicatorReference, {
      id: "4.1.2",
      children: [_jsxs(_components.p, {
        children: ["The ultimate objective of SDG 4 is universal upper secondary ", _jsx(_components.strong, {
          children: "completion"
        }), " and not just enrolment. This is despite the fact that, in many countries, education is only free and compulsory up to lower secondary education at most."]
      }), _jsx(_components.p, {
        children: "The world is not even on track to achieve universal primary completion by the deadline, let alone upper secondary completion. On current trends, fewer than two in three young people will be finishing secondary school in 2030."
      }), _jsxs(_components.p, {
        children: ["The indicator refers to children who may complete education levels up to 3 or 5 years later than the official graduation age – this can be defined as ", _jsx(Tooltip, {
          title: "Timely completion",
          content: "Children who complete education levels up to 3 or 5 years beyond the final grade",
          children: "timely completion"
        }), ". But many children complete even later than that - this can be defined as ", _jsx(Tooltip, {
          title: "Ultimate completion",
          content: "Children who complete education levels up to 8 years beyond the final grade",
          children: "ultimate completion"
        }), "."]
      }), _jsxs(_components.p, {
        children: ["Looking at the graph below, in Angola, for example, there is a nearly 20 percentage point gap between ", _jsx(HighlightByIndex, {
          index: "0",
          children: "timely"
        }), " (64%) and ", _jsx(HighlightByIndex, {
          index: "1",
          children: "ultimate"
        }), " completion (82%) at the primary level."]
      }), _jsx(Figure, {
        id: "COMPLETION"
      })]
    }), "\n", _jsx(_components.h2, {
      id: "conclusion",
      children: "Conclusion"
    }), "\n", _jsx(_components.p, {
      children: "When children enrol, at what age and whether or not they graduate are crucial policy concerns."
    }), "\n", _jsxs(_components.p, {
      children: ["The next themes on this site look at how close we are to achieving ", _jsx(PageLink, {
        name: "equity",
        children: "equity"
      }), " in education, what students are ", _jsx(PageLink, {
        name: "learning",
        children: "learning"
      }), ", how good their education ", _jsx(PageLink, {
        name: "quality",
        children: "quality"
      }), " is, and who ", _jsx(PageLink, {
        name: "finance",
        children: "finances"
      }), " it."]
    }), "\n", _jsx(RelatedMaterial, {
      children: _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://www.education-estimates.org",
            children: "Visualizing Indicators of Education for the World (VIEW)"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://resourcecentre.savethechildren.net/library/education-against-odds-meeting-marginalised-childrens-demands-quality-education",
            children: "Education above the odds – Save the Children"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "http://uis.unesco.org/sites/default/files/documents/ip61-combining-data-out-of-school-children-completion-learning-offer-more-comprehensive-view-sdg4.pdf",
            children: "Combining data on out-of-school children, Completion and learning to offer a more comprehensive view on SDG 4"
          })
        }), "\n"]
      })
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
